import { type ComponentPropsWithoutRef, useState } from 'react';
import { deleteRuntime } from '../../../sources/runtime';
import translations from '../../../translations';
import type { RuntimeListResponse } from '../../../types/runtime';
import { stringReplace } from '../../../utils/string';
import ConfirmModal from '../../generic/modal/ConfirmModal';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';

type LocalRuntimeDeleteModalProps = {
    selectedRuntime: RuntimeListResponse;
    // Wrong type gets inferred when picking onConfirm from ConfirmModal props, so declare a new prop here
    onConfirm: () => void;
} & Pick<ComponentPropsWithoutRef<typeof ConfirmModal>, 'container' | 'onCancel'>;

const LocalRuntimeDeleteModal = ({
    selectedRuntime,
    container,
    onConfirm,
    onCancel,
}: LocalRuntimeDeleteModalProps) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const dispatchNotification = useNotificationDispatch();

    const handleRuntimeDeleteConfirm = async () => {
        try {
            setIsDeleting(true);

            await deleteRuntime(selectedRuntime.id);

            dispatchNotification({
                type: 'notifySuccess',
                payload: {
                    message: stringReplace(
                        translations.LOCAL_RUNTIME_runtime_delete_success_message,
                        {
                            name: selectedRuntime.developerName,
                        },
                    ),
                },
            });

            // Clear selected runtime and hide the delete modal
            onConfirm();
        } catch (error) {
            dispatchNotification({
                type: 'notifyError',
                payload: {
                    error,
                },
            });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <ConfirmModal
            show
            title={translations.LOCAL_RUNTIME_delete_modal_title}
            messages={[
                stringReplace(translations.LOCAL_RUNTIME_delete_modal_message, {
                    connectionName: selectedRuntime?.developerName,
                }),
            ]}
            buttonStyle="danger"
            buttonCaption={translations.COMMON_delete}
            onCancel={onCancel}
            onConfirm={handleRuntimeDeleteConfirm}
            container={container}
            isInProgress={isDeleting}
        />
    );
};

export default LocalRuntimeDeleteModal;
